import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Box, Stack } from '@mui/material';

import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { COLORS } from '@/styles/tokens/colors';

import {
  APPLY_HIGHLIGHT_COMMAND,
  REMOVE_HIGHLIGHT_COMMAND,
} from './highlightCommands';
import {
  HIGHLIGHT_COLOR_LABELS,
  HIGHLIGHT_COLORS,
  HighlightColor,
} from './highlightConstants';

interface HighlightColorMenuProps {
  /** The currently-applied highlight color under the caret, or null. */
  activeHighlightColor: HighlightColor | null;
  /** Called after a color is applied or removed (e.g. to close a parent popover). */
  onAfterSelect?: () => void;
}

/**
 * Color picker menu for highlights. Dispatches APPLY_HIGHLIGHT_COMMAND or
 * REMOVE_HIGHLIGHT_COMMAND on the current Lexical editor when a color is chosen.
 */
export function HighlightColorMenu({
  activeHighlightColor,
  onAfterSelect,
}: HighlightColorMenuProps) {
  const [editor] = useLexicalComposerContext();

  return (
    <Stack py={0.5}>
      <MenuItem
        label="None"
        icon={
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              border: `1px solid ${COLORS.GRAY[300]}`,
            }}
          />
        }
        onClick={() => {
          editor.dispatchCommand(REMOVE_HIGHLIGHT_COMMAND, undefined);
          onAfterSelect?.();
        }}
      />
      {(Object.keys(HIGHLIGHT_COLORS) as HighlightColor[]).map((color) => (
        <MenuItem
          key={color}
          icon={
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: HIGHLIGHT_COLORS[color],
                border:
                  activeHighlightColor === color
                    ? `2px solid ${COLORS.NAVY[600]}`
                    : '1px solid rgba(0,0,0,0.1)',
              }}
            />
          }
          label={HIGHLIGHT_COLOR_LABELS[color]}
          onClick={() => {
            editor.dispatchCommand(APPLY_HIGHLIGHT_COMMAND, color);
            onAfterSelect?.();
          }}
        />
      ))}
    </Stack>
  );
}
